/**
 * 趋势数据 Hook
 */
import { useState, useCallback, useEffect } from 'react';
import { getTrendByDimension, ITrendItem } from '@op/services/weeklyReport';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_REQ } from '@op/utils/format';
import type {
  TRequestBackType,
  TRequestPieBackType,
} from '@op/utils/requestUtil';

interface IUseTrendDataParams {
  filter?: any;
  dimension: 'language' | 'channel';
}

interface IUseTrendDataOptions {
  autoFetch?: boolean; // 是否自动请求
}

type FetchDataParams = { filter?: any; dimension?: string };

type FetchDataFunc = (params?: FetchDataParams) => Promise<void>;

export interface IUseTrendDataResult {
  data: ITrendItem | null;
  loading: boolean;
  error: boolean;
  lineChartData: TRequestBackType | undefined; // 折线图数据
  pieChartData: TRequestPieBackType | undefined; // 饼图数据
  summary: ITrendItem['summary'] | null; // 汇总信息
  fetchData: FetchDataFunc;
}

// ========== 数据处理函数 ==========

/**
 * 将 trend_data 预处理为长格式数据，供 DBO_REQ.listKeys 使用
 */
function preprocessTrendDataToLongFormat(trendData: ITrendItem) {
  const { trend_data: trendDataList } = trendData;

  return trendDataList.map(item => {
    const data: any = { date: item.date };

    item.dimensions.forEach(it => {
      data[it.dimension_value] = it.volume;
    });

    return data;
  });
}

/**
 * 使用 DBO_REQ.listKeys 将 trend_data 转换为折线图格式
 */
function transformTrendDataToLineChart(
  trendData: ITrendItem,
  filter: any,
): TRequestBackType {
  const longFormatData = preprocessTrendDataToLongFormat(trendData);
  return DBO_REQ.moreKeys({
    timeline: longFormatData,
    timeKey: 'date',
    filter,
    timeline_of_start_time: trendData?.timeline_start_time,
    timeline_of_end_time: trendData?.timeline_end_time,
    needTable: true,
    moreLineTable: true,
  });
}

/**
 * 将 pie_data 转换为饼图格式
 *
 * @returns TRequestPieBackType
 *   - series: 饼图系列数据（name + data）
 *   - legend: 图例数据（所有维度值）
 *   - isEmpty: 是否为空
 *   - totalSeries: 总声量
 */
function transformPieDataToPieChart(
  trendData: ITrendItem,
): TRequestPieBackType {
  const { pie_data: pieDataList } = trendData;

  // 先对原始数据排序：other 相关项放最后，其他按值降序
  const sortedList = [...pieDataList].sort((a, b) => {
    const isAOthers = a.dimension_value.toLowerCase() === 'others';
    const isBOthers = b.dimension_value.toLowerCase() === 'others';

    if (isAOthers && !isBOthers) return 1;
    if (!isAOthers && isBOthers) return -1;
    return (b?.volume || 0) - (a?.volume || 0);
  });

  const series: Array<{ name: string; value: number }> = [];
  const legend: string[] = [];
  let totalSeries = 0;

  for (const item of sortedList) {
    const value = item?.volume || 0;
    series.push({ name: item.dimension_value, value });
    legend.push(item.dimension_value);
    totalSeries += value;
  }

  return {
    series,
    legend,
    isEmpty: totalSeries === 0,
    totalSeries,
  };
}

// ========== 分语言声量趋势 / 分渠道声量趋势 Hook ==========

export const useTrendData = (
  { filter, dimension }: IUseTrendDataParams = { dimension: 'language' },
  options?: IUseTrendDataOptions,
): IUseTrendDataResult => {
  const { autoFetch = true } = options || {};
  const [data, setData] = useState<ITrendItem | null>(null);
  const [lineChartData, setLineChartData] = useState<TRequestBackType>();
  const [pieChartData, setPieChartData] = useState<TRequestPieBackType>();
  const [summary, setSummary] = useState<ITrendItem['summary'] | null>(null);

  const { loading, error, wrapperCancelRequest } =
    useCancelRequestHook<ITrendItem>({
      clear: () => {
        setData(null);
        setLineChartData(undefined);
        setPieChartData(undefined);
        setSummary(null);
      },
    });

  const fetchData = useCallback(
    async () => {
      if (!filter?.start_time || !filter?.end_time) {
        return;
      }
      try {
        const res = await wrapperCancelRequest(getTrendByDimension, {
          ...filter,
          group_by: dimension,
          pie_top_n: 10
        });

        const resultData = res || null;
        setData(resultData);

        // 直接处理图表数据
        if (resultData) {
          setLineChartData(transformTrendDataToLineChart(resultData, filter));
          setPieChartData(transformPieDataToPieChart(resultData));
          setSummary(resultData.summary || null);
        } else {
          setLineChartData(undefined);
          setPieChartData(undefined);
          setSummary(null);
        }
      } catch (err) {
        console.error('获取趋势数据失败:', err);
      }
    },
    [filter, dimension], // 依赖 filter 和 dimension
  );

  // 自动请求：filter 存在且 autoFetch 为 true 时才请求
  useEffect(() => {
    if (!filter || !autoFetch) return;
    fetchData();
  }, [autoFetch, fetchData]);

  return {
    data,
    loading,
    error,
    lineChartData,
    pieChartData,
    summary,
    fetchData,
  };
};

export default useTrendData;
